import { useCallback, useEffect, useRef, useState } from "react";
import { v7 as uuidv7 } from "uuid";
import { httpKeepAlive } from "@/api/connection";
import { CreateFileData, FileStatus } from "../files.types";
import { Uploader, UploaderApi } from "../Uploader";

export type UploadQueuePhase =
  | "queued"
  | "uploading"
  | "finalizing"
  | "complete"
  | "error";

export type UploadQueueItem = {
  uuid: string;
  file: File;
  metadata: CreateFileData;
  phase: UploadQueuePhase;
  uploadPercent: number;
  hashPercent: number;
  error?: Error;
};

export type UseUploadQueueOptions = {
  api: UploaderApi;
  concurrency?: number;
  hashFile?: boolean;
  /** Extra metadata merged over the defaults (e.g. source fields). */
  buildMetadata?: (file: File, uuid: string) => Partial<CreateFileData>;
  onUploaded?: (uuid: string, result: unknown) => void;
  onFailed?: (uuid: string, error: Error) => void;
};

export type UseUploadQueueResult = {
  uploads: Record<string, UploadQueueItem>;
  enqueue: (files: File[]) => string[];
  dismiss: (uuid: string) => void;
};

const resolveConcurrency = (value = 3) =>
  Number.isFinite(value) ? Math.max(1, Math.floor(value)) : 3;

/**
 * Runs uploads through `Uploader` with at most `concurrency` active at once.
 * In-flight state lives in the hook, not in the react-query cache, so a
 * refetch cannot wipe it.
 */
export const useUploadQueue = (
  options: UseUploadQueueOptions
): UseUploadQueueResult => {
  const [uploads, setUploads] = useState<Record<string, UploadQueueItem>>({});
  const itemsRef = useRef<Record<string, UploadQueueItem>>({});
  const pendingRef = useRef<string[]>([]);
  // null while the Uploader is being constructed (its constructor can settle)
  const activeRef = useRef<Map<string, Uploader | null>>(new Map());
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const patch = useCallback(
    (uuid: string, changes: Partial<UploadQueueItem>) => {
      const item = itemsRef.current[uuid];
      if (!item) return;
      const unchanged = (
        Object.keys(changes) as (keyof UploadQueueItem)[]
      ).every((k) => item[k] === changes[k]);
      if (unchanged) return; // progress events repeat rounded percentages
      itemsRef.current = {
        ...itemsRef.current,
        [uuid]: { ...item, ...changes },
      };
      setUploads(itemsRef.current);
    },
    []
  );

  // abort() only stops local work, so release the server row best effort.
  // `finalizing` means completeUpload was invoked and abort() cannot cancel
  // it, so marking the row FAILED could overwrite a finished upload.
  const markFailed = useCallback((uuid: string) => {
    if (itemsRef.current[uuid]?.phase === "finalizing") return;
    try {
      Promise.resolve(optionsRef.current.api.markFailed?.({ uuid })).catch(
        () => {}
      );
    } catch {
      // nothing to clean up
    }
  }, []);

  const pump = useCallback(() => {
    const limit = resolveConcurrency(optionsRef.current.concurrency);
    while (activeRef.current.size < limit && pendingRef.current.length > 0) {
      const uuid = pendingRef.current.shift() as string;
      const item = itemsRef.current[uuid];
      if (!item) continue;

      const settle = (changes: Partial<UploadQueueItem>) => {
        activeRef.current.delete(uuid);
        patch(uuid, changes);
        pump();
      };

      patch(uuid, { phase: "uploading" });
      activeRef.current.set(uuid, null); // reserve the slot before construction
      // The constructor starts the upload, so it is only built once a slot is
      // free. The map entry is set after construction; callbacks fire async.
      const uploader = new Uploader({
        file: item.file,
        metadata: item.metadata,
        hashFile: optionsRef.current.hashFile ?? true,
        api: {
          ...optionsRef.current.api,
          // Uploader only calls this once every part and the hash are done
          completeUpload: (data) => {
            patch(uuid, { phase: "finalizing" });
            return optionsRef.current.api.completeUpload(data);
          },
        },
        onProgress: (progress) => {
          const current = itemsRef.current[uuid];
          if (!current) return;
          // an empty file reports NaN (0 / 0)
          const percent = Number.isFinite(progress.percentage)
            ? progress.percentage
            : item.file.size === 0
            ? 100
            : 0;
          const uploadPercent =
            progress.type === "upload" ? percent : current.uploadPercent;
          const hashPercent =
            progress.type === "hash" ? percent : current.hashPercent;
          patch(uuid, { uploadPercent, hashPercent });
        },
        onComplete: (result) => {
          settle({ phase: "complete", uploadPercent: 100 });
          optionsRef.current.onUploaded?.(uuid, result);
        },
        onError: (error) => {
          settle({ phase: "error", error });
          optionsRef.current.onFailed?.(uuid, error);
        },
      });
      // onError can fire inside the constructor, which already freed the slot
      if (activeRef.current.has(uuid)) activeRef.current.set(uuid, uploader);
    }
  }, [patch]);

  const enqueue = useCallback(
    (files: File[]) => {
      if (files.length === 0) return [];
      const { buildMetadata } = optionsRef.current;
      const added: Record<string, UploadQueueItem> = {};

      const uuids = files.map((file) => {
        const uuid = uuidv7();
        added[uuid] = {
          uuid,
          file,
          metadata: {
            name: file.name,
            size: file.size,
            ext: file.name.split(".").pop() || "",
            client_modified: new Date(file.lastModified).toISOString(),
            is_folder: false,
            status: FileStatus.UPLOADING,
            ...buildMetadata?.(file, uuid),
            uuid,
          },
          phase: "queued",
          uploadPercent: 0,
          // nothing to wait for when the file is not hashed
          hashPercent: optionsRef.current.hashFile ?? true ? 0 : 100,
        };
        return uuid;
      });

      itemsRef.current = { ...itemsRef.current, ...added };
      setUploads(itemsRef.current);
      pendingRef.current.push(...uuids);

      httpKeepAlive(); // once per batch
      pump();
      return uuids;
    },
    [pump]
  );

  const dismiss = useCallback(
    (uuid: string) => {
      if (!itemsRef.current[uuid]) return;
      pendingRef.current = pendingRef.current.filter((id) => id !== uuid);
      const wasActive = activeRef.current.has(uuid);
      if (wasActive) {
        // abort() reports neither completion nor error, so free the slot here
        activeRef.current.get(uuid)?.abort();
        activeRef.current.delete(uuid);
        markFailed(uuid);
      }
      const { [uuid]: _removed, ...rest } = itemsRef.current;
      itemsRef.current = rest;
      setUploads(rest);
      if (wasActive) pump();
    },
    [pump, markFailed]
  );

  useEffect(() => {
    const active = activeRef.current;
    return () => {
      pendingRef.current = [];
      active.forEach((uploader, uuid) => {
        uploader?.abort();
        markFailed(uuid);
      });
      active.clear();
    };
  }, [markFailed]);

  return { uploads, enqueue, dismiss };
};
